import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Square, Play, Pause, Headphones, ChevronLeft, Users, Loader2, Sparkles, Share2, Save, RotateCcw } from "lucide-react";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Recorder, MixPlayer, renderMix, defaultMix, MIX_PRESETS } from "@/lib/audioEngine";
import { backendUrl } from "@/components/SongCard";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import Logo from "@/components/Logo";

export default function Duet() {
  const { parentId } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [phase, setPhase] = useState("prep");
  const [count, setCount] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [recId, setRecId] = useState(null);
  const [vocalBlob, setVocalBlob] = useState(null);
  const [mix, setMix] = useState(defaultMix());
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [privacy, setPrivacy] = useState("public");
  const backingUrl = `${API}/recordings/${parentId}/audio`;

  const recorderRef = useRef(null);
  const audioRef = useRef(null);
  const mixPlayerRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/duets/${parentId}`).then(({ data }) => setInfo(data)).catch(() => { toast.error("This duet isn't available."); nav("/community"); });
    return () => { recorderRef.current?.destroy(); mixPlayerRef.current?.destroy(); clearInterval(timerRef.current); };
  }, [parentId, nav]);

  useEffect(() => {
    const iv = setInterval(() => { if (recorderRef.current) setLevel(recorderRef.current.getLevel()); }, 90);
    return () => clearInterval(iv);
  }, []);

  const begin = async () => {
    try { recorderRef.current = new Recorder(); await recorderRef.current.init(false); }
    catch { toast.error("Microphone access is required. Please allow permission and try again."); return; }
    try {
      const { data } = await api.post("/duets/start", { parent_id: parentId });
      setRecId(data.recording_id); await refresh();
    } catch (e) {
      recorderRef.current?.destroy();
      if (e?.response?.status === 402) { setPhase("limit"); return; }
      toast.error(apiErr(e)); return;
    }
    setPhase("countdown");
    let c = 3; setCount(3);
    const cd = setInterval(() => { c -= 1; if (c <= 0) { clearInterval(cd); startRec(); } else setCount(c); }, 1000);
  };

  const startRec = () => {
    setPhase("recording");
    recorderRef.current.start();
    if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(() => {}); }
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(audioRef.current?.currentTime || 0), 200);
  };

  const stopRec = async () => {
    clearInterval(timerRef.current);
    audioRef.current?.pause();
    const blob = await recorderRef.current.stop();
    setVocalBlob(blob);
    mixPlayerRef.current = new MixPlayer();
    await mixPlayerRef.current.load(blob, backingUrl);
    setPhase("mixer");
  };

  const togglePreview = () => {
    const mp = mixPlayerRef.current; if (!mp) return;
    if (previewPlaying) { mp.stop(); setPreviewPlaying(false); }
    else { mp.play(mix, () => setPreviewPlaying(false)); setPreviewPlaying(true); }
  };

  const doRender = async () => {
    mixPlayerRef.current?.stop(); setPreviewPlaying(false);
    setPhase("rendering"); setProgress(5);
    try {
      const { blob, duration } = await renderMix(vocalBlob, backingUrl, mix, setProgress);
      const fd = new FormData();
      fd.append("audio", blob, "duet.wav");
      fd.append("duration", String(Math.round(duration)));
      fd.append("mix_settings", JSON.stringify(mix));
      fd.append("status", "completed");
      await api.post(`/recordings/${recId}/save`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/recordings/${recId}`, { privacy: "public" }).catch(() => {});
      setPhase("done"); toast.success("Duet ready!");
    } catch { setPhase("mixer"); toast.error("We couldn't finish your duet. Your vocal is safe. Please try again."); }
  };

  const publish = async (p) => {
    setPrivacy(p);
    try { await api.patch(`/recordings/${recId}`, { privacy: p }); toast.success(p === "public" ? "Duet published!" : "Privacy updated."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const share = () => {
    const url = `${window.location.origin}/performance/${recId}`;
    if (navigator.share) navigator.share({ url }).catch(() => {}); else { navigator.clipboard.writeText(url); toast.success("Link copied!"); }
  };

  if (!info) return <div className="min-h-screen bg-mesh" />;
  const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-mesh">
      <div className="glass border-b border-white/10 px-4 h-16 flex items-center justify-between sticky top-0 z-40">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white" data-testid="duet-back"><ChevronLeft className="w-5 h-5" /> Exit</button>
        <Logo size={34} />
        <div className="w-16" />
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-6">
          <img src={info.cover_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-neon-cyan"><Users className="w-3.5 h-3.5" /> DUET</span>
            <h1 className="font-head text-xl font-bold truncate">{info.song_title}</h1>
            <p className="text-sm text-muted-foreground truncate">Singing with @{info.owner?.username}</p>
          </div>
        </div>

        <audio ref={audioRef} src={backingUrl} onEnded={() => phase === "recording" && stopRec()} onError={() => phase === "recording" && toast.message("Original track couldn't load.")} />

        {phase === "prep" && (
          <div className="space-y-6 fade-up">
            <div className="glass rounded-2xl p-5 flex items-start gap-3 border-neon-cyan/30">
              <Headphones className="w-6 h-6 text-neon-cyan flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">Use headphones so @{info.owner?.username}'s vocals don't leak into your mic. You'll sing along to their performance and your voices will be mixed together.</p>
            </div>
            <Button onClick={begin} data-testid="duet-start" className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold py-7 text-base"><Mic className="w-5 h-5 mr-2" /> Allow mic & Join duet</Button>
          </div>
        )}

        {phase === "limit" && (
          <div className="glass rounded-3xl p-10 text-center fade-up">
            <h2 className="font-head text-2xl font-extrabold">You've used your 2 free recordings.</h2>
            <p className="text-muted-foreground mt-2 mb-6">Upgrade to Premium to keep singing.</p>
            <Button onClick={() => nav("/premium")} className="rounded-full bg-neon-pink glow-pink font-bold px-8">Go Premium</Button>
          </div>
        )}

        {phase === "countdown" && (
          <div className="flex flex-col items-center justify-center py-20"><div className="text-8xl font-head font-black text-gradient animate-pulse">{count}</div><p className="text-muted-foreground mt-4">Get ready...</p></div>
        )}

        {phase === "recording" && (
          <div className="space-y-6 fade-up">
            <div className="flex items-center justify-center gap-2 text-neon-pink font-bold"><span className="w-3 h-3 rounded-full bg-neon-pink animate-pulse" /> RECORDING · {fmt(elapsed)}</div>
            <div className="card-surface rounded-2xl p-6"><div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-green-400 to-neon-pink transition-[width] duration-75" style={{ width: `${Math.min(100, level * 100)}%` }} /></div><p className="text-xs text-muted-foreground mt-2 text-center">Sing along with @{info.owner?.username}</p></div>
            <Button onClick={stopRec} data-testid="duet-stop" className="w-full rounded-full bg-white text-black hover:bg-white/90 font-bold py-7"><Square className="w-5 h-5 mr-2 fill-black" /> Stop & Mix</Button>
          </div>
        )}

        {phase === "mixer" && (
          <div className="space-y-6 fade-up">
            <h2 className="font-head text-2xl font-extrabold">Mix your duet</h2>
            <Button onClick={togglePreview} data-testid="duet-preview" className="w-full rounded-full glass border border-white/20 hover:bg-white/10 font-bold py-6">{previewPlaying ? <><Pause className="w-5 h-5 mr-2" /> Stop preview</> : <><Play className="w-5 h-5 mr-2" /> Preview duet</>}</Button>
            <div className="flex flex-wrap gap-2">{Object.keys(MIX_PRESETS).map((p) => <button key={p} onClick={() => setMix({ ...mix, ...MIX_PRESETS[p], preset: p })} className={`px-4 py-2 rounded-full text-sm font-medium border ${mix.preset === p ? "bg-neon-pink border-neon-pink" : "border-white/15 text-muted-foreground"}`}>{p}</button>)}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Your Vocal</span><span className="text-neon-cyan font-mono">{Math.round(mix.vocalVolume * 100)}%</span></div><Slider value={[mix.vocalVolume]} min={0} max={2} step={0.05} onValueChange={(v) => setMix({ ...mix, vocalVolume: v[0] })} data-testid="duet-vocal-vol" /></div>
              <div><div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Partner + Music</span><span className="text-neon-cyan font-mono">{Math.round(mix.musicVolume * 100)}%</span></div><Slider value={[mix.musicVolume]} min={0} max={2} step={0.05} onValueChange={(v) => setMix({ ...mix, musicVolume: v[0] })} /></div>
              <div><div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Reverb</span><span className="text-neon-cyan font-mono">{Math.round(mix.reverb.amount * 100)}%</span></div><Slider value={[mix.reverb.amount]} min={0} max={1} step={0.02} onValueChange={(v) => setMix({ ...mix, reverb: { ...mix.reverb, amount: v[0] } })} /></div>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setMix(defaultMix())} className="rounded-full"><RotateCcw className="w-4 h-4 mr-2" /> Reset</Button>
              <Button onClick={doRender} data-testid="duet-render" className="flex-1 rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold py-6">Render Duet</Button>
            </div>
          </div>
        )}

        {phase === "rendering" && (
          <div className="flex flex-col items-center justify-center py-20 fade-up"><Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-6" /><p className="font-head text-xl font-bold">Mixing your duet... {Math.round(progress)}%</p><div className="w-full max-w-sm h-2 rounded-full bg-white/10 overflow-hidden mt-4"><div className="h-full bg-gradient-to-r from-neon-pink to-neon-cyan" style={{ width: `${progress}%` }} /></div></div>
        )}

        {phase === "done" && (
          <div className="space-y-6 fade-up">
            <div className="glass rounded-3xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-neon-pink/20 flex items-center justify-center mx-auto mb-4 glow-pink"><Sparkles className="w-8 h-8 text-neon-pink" /></div>
              <h2 className="font-head text-2xl font-extrabold">Your duet is ready!</h2>
              <p className="text-muted-foreground mt-1">with @{info.owner?.username}</p>
              <audio controls src={`${API}/recordings/${recId}/audio`} className="w-full mt-6" data-testid="duet-final" />
            </div>
            <div className="card-surface rounded-2xl p-5">
              <p className="text-sm font-semibold mb-3">Who can see this?</p>
              <div className="grid grid-cols-3 gap-2">{[["public", "Public"], ["unlisted", "Link only"], ["private", "Private"]].map(([v, l]) => <button key={v} onClick={() => publish(v)} className={`py-2.5 rounded-xl text-sm font-medium border ${privacy === v ? "bg-neon-pink border-neon-pink" : "border-white/15 text-muted-foreground"}`}>{l}</button>)}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Button onClick={share} variant="outline" className="rounded-full glass border-white/20"><Share2 className="w-4 h-4 mr-2" /> Share</Button>
              <Button onClick={() => nav("/recordings")} variant="outline" className="rounded-full glass border-white/20"><Save className="w-4 h-4 mr-2" /> Recordings</Button>
              <Button onClick={() => nav("/community")} className="rounded-full bg-neon-pink font-bold">Community</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
